/**
 * Pure permission-check helpers. No React, no hooks — safe to call
 * from any context (hooks, route matchers, non-component utilities).
 *
 * WHY THIS EXISTS
 * Roles in MongoDB grant `{resource}.view.all` or `{resource}.view.own`
 * (e.g. the director role has `opportunities.view.all`). Earlier frontend
 * gates asked for a bare `{resource}.view` key that never existed in any
 * role seed, so director users were denied pages they should see. These
 * helpers collapse all three conventional forms into a single resource
 * check and are the single source of truth for view/edit gating.
 *
 * TODO (tech debt, not this PR): several page components still use
 * `['admin', 'director'].includes(user.role)` role-string gates
 * (Accounts.js, Invoices.js, FieldServices.js, Contracts.js edit/create
 * buttons). Works today, but any new role (e.g. 'regional_director',
 * 'sales_manager') that should have the same access will silently miss
 * out. Migrate those to canViewResource / canEditResource when touching
 * those files.
 */

const ADMIN_WILDCARD = '*';

function hasWildcard(perms) {
  return Array.isArray(perms) && perms.includes(ADMIN_WILDCARD);
}

/**
 * True if `perms` grants view access to `resource`. Matches any of
 * `${resource}.view`, `${resource}.view.all`, `${resource}.view.own`.
 * Admin wildcard (`*`) short-circuits.
 */
export function canViewResource(perms, resource) {
  if (!Array.isArray(perms)) return false;
  if (hasWildcard(perms)) return true;
  return (
    perms.includes(`${resource}.view`) ||
    perms.includes(`${resource}.view.all`) ||
    perms.includes(`${resource}.view.own`)
  );
}

/**
 * True if `perms` grants edit access to `resource`. Matches any of
 * `${resource}.edit`, `${resource}.edit.all`, `${resource}.edit.own`.
 * Admin wildcard (`*`) short-circuits.
 *
 * NOTE: this is ownership-BLIND — it only sees the permission list, not a
 * record. It answers "can this user edit *some* records of this type?",
 * which is right for showing/hiding a whole section but WRONG for per-row
 * gating. For a specific record use `canEditAccount` / `canEditContact`,
 * which fold in ownership the same way the backend does.
 */
export function canEditResource(perms, resource) {
  if (!Array.isArray(perms)) return false;
  if (hasWildcard(perms)) return true;
  return (
    perms.includes(`${resource}.edit`) ||
    perms.includes(`${resource}.edit.all`) ||
    perms.includes(`${resource}.edit.own`)
  );
}

/**
 * Fields that denote record ownership. Mirrors `verify_ownership` in
 * backend/auth_helper.py: a user owns a record if their id appears in any
 * of these assignment fields. Keep in sync with that list.
 */
const OWNERSHIP_FIELDS = [
  'assigned_sales_rep_id',
  'assigned_agent_id',
  'deal_owner_id',
  'user_id',
  'created_by',
];

/**
 * True if `userId` owns `record` — i.e. their id is in one of `fields`
 * (defaults to the full OWNERSHIP_FIELDS set). A missing record is never
 * owned (returns false), which keeps callers on the safe "hide" side.
 */
export function ownsRecord(record, userId, fields = OWNERSHIP_FIELDS) {
  if (!record || !userId) return false;
  return fields.some((f) => record[f] === userId);
}

/**
 * Per-record edit gate for an account. Mirrors the backend rule in
 * crm.py::update_account (+ verify_ownership): admin/`accounts.edit.all`
 * may edit any account; `accounts.edit.own` may edit only owned accounts.
 */
export function canEditAccount(perms, account, userId) {
  if (!Array.isArray(perms)) return false;
  if (hasWildcard(perms)) return true;
  if (perms.includes('accounts.edit.all')) return true;
  if (perms.includes('accounts.edit.own')) return ownsRecord(account, userId);
  return false;
}

/**
 * Per-record edit/delete gate for a contact. Mirrors can_edit_contact in
 * backend/routes/contacts.py: admin/`contacts.edit.all` may edit any
 * contact; `contacts.edit.own` may edit a contact only if they own its
 * PARENT ACCOUNT — and the backend checks only the account's
 * assigned_sales_rep_id / assigned_agent_id, so we restrict to those two.
 * Delete uses the same rule (the backend delete path is can_edit_contact,
 * not a separate `contacts.delete` perm).
 */
export function canEditContact(perms, parentAccount, userId) {
  if (!Array.isArray(perms)) return false;
  if (hasWildcard(perms)) return true;
  if (perms.includes('contacts.edit.all')) return true;
  if (perms.includes('contacts.edit.own')) {
    return ownsRecord(parentAccount, userId, ['assigned_sales_rep_id', 'assigned_agent_id']);
  }
  return false;
}
